import { p } from "./_helpers";

const agriculture = {
 slug: "agriculture",
 path: "/secteurs/agriculture",
 hero: {
 image: "/photos/sector-agriculture-hero.jpg",
 alt: p("Guinée-Bissau, agriculture tropicale", "Guinea-Bissau, tropical agriculture", "Guiné-Bissau, agricultura tropical"),
 breadcrumb: p("Agriculture", "Agriculture", "Agricultura"),
 title: p("Investir dans l'agriculture.", "Investing in agriculture.", "Investir na agricultura."),
 subtitle: p(
 "Mangue, riz, agrumes, maraîchage, apiculture : une productivité rare, encore peu transformée.",
 "Mango, rice, citrus, market gardening, beekeeping: rare productivity, still lightly processed.",
 "Manga, arroz, citrinos, horticultura, apicultura: uma produtividade rara, ainda pouco transformada."
 ),
 },
 seo: {
 title: p(
 "Investir dans l'agriculture en Guinée-Bissau | NANDA SÁ",
 "Investing in agriculture in Guinea-Bissau | NANDA SÁ",
 "Investir na agricultura na Guiné-Bissau | NANDA SÁ"
 ),
 description: p(
 "Panorama agricole en Guinée-Bissau : mangue, riz, agrumes, maraîchage, apiculture. Chiffres clés, opportunités, défis et passerelles NANDA SÁ.",
 "Agricultural panorama of Guinea-Bissau: mango, rice, citrus, market gardening, beekeeping. Key figures, opportunities, challenges and NANDA SÁ pathways.",
 "Panorama agrícola da Guiné-Bissau: manga, arroz, citrinos, horticultura, apicultura. Dados-chave, oportunidades, desafios e pontes NANDA SÁ."
 ),
 keywords: "agriculture Guinée-Bissau, mangue, riz, agrumes, maraîchage, apiculture, agroalimentaire Afrique de l'Ouest",
 },
 presentation: {
 title: p("Une agriculture riche et sous-exploitée.", "A rich and underexploited agriculture.", "Uma agricultura rica e sub-explorada."),
 paragraphs: p(
 [
 "Au-delà du cajou, la Guinée-Bissau dispose de sols fertiles, d'un climat tropical humide idéal pour la mangue, les agrumes, le riz, le maraîchage et l'apiculture. Les mangues bissau-guinéennes sont régulièrement citées parmi les meilleures d'Afrique de l'Ouest, et pourtant, elles restent presque entièrement destinées au marché local ou perdues faute de transformation.",
 "Le pays combine deux atouts rares : une productivité agroécologique naturelle et une main-d'œuvre rurale nombreuse. Ce qui manque, c'est la transformation, la logistique du froid et la mise en marché, autant d'espaces d'investissement immédiats.",
 "Prolongée au nord par la Guinée-Bissau, cette agriculture forme un continuum agroécologique transfrontalier unique : mêmes sols, mêmes cycles pluvieux, mêmes traditions paysannes. L'eau (voir notre article dédié dans le Carnet), l'énergie solaire (voir /secteurs/energie) et l'organisation coopérative en sont les trois leviers convergents. Toute exploitation qui les articule intelligemment dépasse rapidement la moyenne régionale de rendement.",
 ],
 [
 "Beyond cashew, Guinea-Bissau enjoys fertile soils and a humid tropical climate ideal for mango, citrus, rice, market gardening and beekeeping. Bissau-Guinean mangoes are regularly cited among the best in West Africa, yet they remain almost entirely destined for the local market or lost due to lack of processing.",
 "The country combines two rare assets: natural agroecological productivity and a large rural workforce. What is missing is processing, cold logistics and market access, as many immediate investment spaces.",
 "Extended northwards by Bissau-Guinean Guinée-Bissau, this agriculture forms a unique cross-border agroecological continuum: same soils, same rainy cycles, same peasant traditions. Water (see our dedicated Carnet article), solar energy (see /secteurs/energie) and cooperative organisation are its three converging levers. Any farm that intelligently articulates them quickly exceeds the regional yield average.",
 ],
 [
 "Para além do caju, a Guiné-Bissau dispõe de solos férteis e de um clima tropical húmido ideal para a manga, os citrinos, o arroz, a horticultura e a apicultura. As mangas guineenses são regularmente citadas entre as melhores da África Ocidental e, no entanto, permanecem quase inteiramente destinadas ao mercado local ou perdidas por falta de transformação.",
 "O país combina dois trunfos raros: uma produtividade agroecológica natural e uma mão-de-obra rural numerosa. O que falta é a transformação, a logística do frio e a comercialização, outros tantos espaços de investimento imediatos.",
 "Prolongada a norte pea Guiné-Bissau, esta agricultura forma um continuum agroecológico transfronteiriço único: mesmos solos, mesmos ciclos chuvosos, mesmas tradições camponesas. A água (ver o nosso artigo dedicado no Caderno), a energia solar (ver /secteurs/energie) e a organização cooperativa são as três alavancas convergentes. Qualquer exploração que as articule inteligentemente ultrapassa rapidamente a média regional de rendimento.",
 ]
 ),
 },
 kpis: {
 title: p("Chiffres clés de l'agriculture", "Agriculture key figures", "Dados-chave da agricultura"),
 items: [
 { n: "45 %", l: p("Du PIB agricole", "Of agricultural GDP", "Do PIB agrícola"), d: p("L'agriculture représente près de la moitié de l'économie.", "Agriculture is nearly half the economy.", "A agricultura representa quase metade da economia.") },
 { n: "60 %+", l: p("De la main-d'œuvre", "Of the workforce", "Da mão-de-obra"), d: p("Emploi rural majoritaire.", "Predominantly rural employment.", "Emprego rural maioritário.") },
 { n: "3", l: p("Récoltes de mangue / an", "Mango harvests / year", "Colheitas de manga / ano"), d: p("Grâce à la diversité variétale.", "Thanks to varietal diversity.", "Graças à diversidade varietal.") },
 { n: "180 000t", l: p("De riz produites / an", "Tonnes of rice / year", "Toneladas de arroz / ano"), d: p("Culture stratégique, encore importateur net.", "Strategic crop, still net importer.", "Cultura estratégica, ainda importador líquido.") },
 { n: "> 30 %", l: p("Pertes post-récolte", "Post-harvest losses", "Perdas pós-colheita"), d: p("Faute de froid et de transformation. Une opportunité claire.", "Lack of cold and processing. A clear opportunity.", "Falta de frio e transformação. Uma clara oportunidade.") },
 { n: "Rare", l: p("Bio certifié à l'export", "Certified organic for export", "Bio certificado para exportação"), d: p("Créneau premium peu occupé.", "Premium niche largely open.", "Nicho premium largamente aberto.") },
 ],
 sources: p(
 "Sources : FAO, Banque mondiale, ministère de l'Agriculture de la Guinée-Bissau, données 2023-2025.",
 "Sources: FAO, World Bank, Guinea-Bissau Ministry of Agriculture, data 2023-2025.",
 "Fontes: FAO, Banco Mundial, Ministério da Agricultura da Guiné-Bissau, dados 2023-2025."
 ),
 },
 opportunites: {
 title: p("Segments d'investissement prioritaires", "Priority investment segments", "Segmentos de investimento prioritários"),
 intro: p(
 "Quatre segments à fort potentiel, avec des tickets d'entrée variés et des débouchés sécurisés.",
 "Four high-potential segments, with varied entry tickets and secured outlets.",
 "Quatro segmentos de forte potencial, com tickets de entrada variados e escoamentos assegurados."
 ),
 segments: [
 { h3: p("1. Transformation de la mangue", "1. Mango processing", "1. Transformação da manga"),
 p: p("Jus, pulpes, séchée, purée, chutneys. Marchés local et export. Ticket : 100 000 à 500 000 €.", "Juice, pulp, dried, purée, chutneys. Local and export markets. Ticket: €100k to €500k.", "Sumo, polpa, seca, puré, chutneys. Mercados local e exportação. Ticket: 100 000 a 500 000 €.") },
 { h3: p("2. Riziculture moderne (paddy & étuvage)", "2. Modern rice cultivation (paddy & parboiling)", "2. Cultura do arroz moderna (paddy e parboiling)"),
 p: p("Le pays importe encore une part significative de son riz. Substitution locale à forte valeur. Ticket : 300 000 à 1,5 M€.", "The country still imports a significant share of its rice. Local substitution with high value. Ticket: €300k to €1.5M.", "O país ainda importa uma parte significativa do seu arroz. Substituição local de alto valor. Ticket: 300 000 a 1,5 M€.") },
 { h3: p("3. Maraîchage sous serre & irrigation solaire", "3. Greenhouse market gardening & solar irrigation", "3. Horticultura em estufa e irrigação solar"),
 p: p("Approvisionnement Bissau + Bafatá + Gabú. Modèle rentable dès 5 hectares. Ticket : 50 000 à 300 000 €.", "Supplying Bissau + Bafatá + Gabú. Profitable from 5 hectares. Ticket: €50k to €300k.", "Abastecimento Bissau + Bissau via Guiné-Bissau. Modelo rentável a partir de 5 hectares. Ticket: 50 000 a 300 000 €.") },
 { h3: p("4. Apiculture & miel de mangrove", "4. Beekeeping & mangrove honey", "4. Apicultura e mel de mangal"),
 p: p("Miel de mangrove reconnu, faible concurrence, marché premium européen. Ticket : 20 000 à 100 000 €.", "Recognised mangrove honey, low competition, premium European market. Ticket: €20k to €100k.", "Mel de mangal reconhecido, baixa concorrência, mercado premium europeu. Ticket: 20 000 a 100 000 €.") },
 ],
 },
 defis: {
 title: p("Les défis à anticiper", "Challenges to anticipate", "Desafios a antecipar"),
 intro: p(
 "L'agriculture est un métier de patience, plus encore en Guinée-Bissau.",
 "Agriculture is a business of patience, even more so in Guinea-Bissau.",
 "A agricultura é um ofício de paciência, ainda mais na Guiné-Bissau."
 ),
 items: p(
 [
 "Une saisonnalité marquée qui impose une trésorerie robuste.",
 "Une logistique du froid quasi absente, à construire souvent en interne.",
 "Un accès au foncier agricole à négocier avec les communautés locales.",
 "Une productivité qui reste bonne à l'hectare, mais dispersée sur de petites exploitations.",
 "Un cadre certification bio (Ecocert, Écocert, etc.) à structurer depuis zéro.",
 ],
 [
 "A marked seasonality requiring robust cash flow.",
 "A near-absent cold chain, often to be built internally.",
 "Access to agricultural land to negotiate with local communities.",
 "Productivity remains good per hectare but scattered on small farms.",
 "An organic certification framework (Ecocert, etc.) to build from scratch.",
 ],
 [
 "Uma sazonalidade marcada que exige uma tesouraria robusta.",
 "Uma cadeia de frio quase ausente, frequentemente a construir internamente.",
 "Um acesso ao imóvel agrícola a negociar com as comunidades locais.",
 "Uma produtividade que permanece boa por hectare, mas dispersa em pequenas explorações.",
 "Um enquadramento de certificação bio (Ecocert, etc.) a estruturar desde zero.",
 ]
 ),
 closing: p(
 "Ces défis se lèvent avec un partenaire local, patient et méthodique. C'est le rôle que NANDA SÁ joue depuis 2023.",
 "These challenges can be lifted with a local, patient and methodical partner. That is the role NANDA SÁ plays since 2023.",
 "Estes desafios levantam-se com um parceiro local, paciente e metódico. É o papel que a NANDA SÁ desempenha desde 2023."
 ),
 },
 pourquoi: {
 title: p("Un ancrage rural et une exigence de transformation.", "Rural roots and a demand for processing.", "Uma ancoragem rural e uma exigência de transformação."),
 paragraphs: p(
 [
 "NANDA SÁ travaille au quotidien avec les coopératives et les producteurs de la région de Cacheu, et bâtit à travers NANDA SÁ la démonstration qu'une transformation locale exigeante est possible et rentable.",
 "Nous accompagnons les investisseurs souhaitant lancer un projet agricole ou agroalimentaire, depuis l'identification du foncier jusqu'à la certification export.",
 ],
 [
 "NANDA SÁ works daily with cooperatives and producers of the Cacheu region, and builds through NANDA SÁ the proof that demanding local processing is possible and profitable.",
 "We support investors wishing to launch an agricultural or agri-food project, from land identification to export certification.",
 ],
 [
 "A NANDA SÁ trabalha quotidianamente com as cooperativas e os produtores da região de Cacheu, e constrói através da NANDA SÁ a prova de que uma transformação local exigente é possível e rentável.",
 "Acompanhamos os investidores que desejam lançar um projecto agrícola ou agroalimentar, desde a identificação do imobiliário até à certificação para exportação.",
 ]
 ),
 bullets: p(
 [
 "Étude de faisabilité agricole ou agroalimentaire",
 "Négociation foncière avec les communautés",
 "Structuration des filières et coopératives partenaires",
 "Certification biologique et normes export",
 "Chaîne de froid et logistique portuaire",
 ],
 [
 "Agricultural or agri-food feasibility study",
 "Land negotiation with communities",
 "Value chain structuring and partner cooperatives",
 "Organic certification and export standards",
 "Cold chain and port logistics",
 ],
 [
 "Estudo de viabilidade agrícola ou agroalimentar",
 "Negociação fundiária com as comunidades",
 "Estruturação de fileiras e cooperativas parceiras",
 "Certificação biológica e normas de exportação",
 "Cadeia de frio e logística portuária",
 ]
 ),
 },
 download: {
 title: p("Télécharger la note « Agriculture · Guinée-Bissau ».", "Download the brief « Agriculture · Guinea-Bissau ».", "Descarregar a nota « Agricultura · Guiné-Bissau »."),
 body: p(
 "Une note préparée par NANDA SÁ : chiffres clés, segments d'investissement, cadre foncier et contacts utiles.",
 "A brief prepared by NANDA SÁ: key figures, investment segments, land framework and useful contacts.",
 "Uma nota preparada pela NANDA SÁ: dados-chave, segmentos de investimento, enquadramento fundiário e contactos úteis."
 ),
 pdfUrl: "/pdf/nanda-sa-note-agriculture.pdf",
 pdfFilename: "nanda-sa-note-agriculture.pdf",
 mailSubject: p("Téléchargement de la note « Agriculture · Guinée-Bissau »", "Download of the brief « Agriculture · Guinea-Bissau »", "Descarga da nota « Agricultura · Guiné-Bissau »"),
 mailBody: p(
 "Bonjour, je souhaite recevoir la note « Agriculture · Guinée-Bissau ». Merci de me tenir informé(e) des prochaines publications de NANDA SÁ.",
 "Hello, I would like to receive the brief « Agriculture · Guinea-Bissau ». Please keep me informed of NANDA SÁ's next publications.",
 "Olá, gostaria de receber a nota « Agricultura · Guiné-Bissau ». Por favor, mantenha-me informado(a) das próximas publicações da NANDA SÁ."
 ),
 },
 contact: {
 title: p("Parlons de votre projet agricole.", "Let's talk about your agricultural project.", "Falemos do seu projecto agrícola."),
 body: p(
 "Agriculteur, transformateur, exportateur, ONG, coopérative, diaspora : décrivez votre intention, nous répondons personnellement.",
 "Farmer, processor, exporter, NGO, cooperative, diaspora: describe your intention, we respond personally.",
 "Agricultor, transformador, exportador, ONG, cooperativa, diáspora: descreva a sua intenção, respondemos pessoalmente."
 ),
 quote: p(
 "La terre est le premier capital. Encore faut-il savoir la faire fructifier durablement.",
 "Land is the first capital. It still has to be made productive sustainably.",
 "A terra é o primeiro capital. Ainda assim, é preciso saber torná-la produtiva de forma sustentável."
 ),
 subject: p("Investir dans l'agriculture en Guinée-Bissau", "Invest in agriculture in Guinea-Bissau", "Investir na agricultura na Guiné-Bissau"),
 },
 ctas: ["NandaSa", "Conseil"],
};

export default agriculture;
